import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';

import { api } from '../api/client';
import type { CatalogueEntry } from '../api/types';
import { useApi } from '../api/useApi';
import { useDiscoveryState } from '../api/useDiscoveryState';
import { useAuth } from '../auth/AuthContext';
import AsyncSection from '../components/AsyncSection';
import MediaCard from '../components/media/MediaCard';
import MediaGrid from '../components/media/MediaGrid';
import { CatalogueCard } from '../components/media/CatalogueBrowser';
import { useOpenCatalogueEntry } from '../components/media/useOpenCatalogueEntry';
import CatalogueDialog from '../components/media/CatalogueDialog';
import LiveTvSearchResults from '../components/livetv/LiveTvSearchResults';

/// One search across everything the server holds - and, where the catalogue stands in for the
/// library, across everything that exists.
///
/// It matches the sort title, so case and a leading article never get in the way: "matrix"
/// finds "The Matrix". Where the server shows the catalogue as the library, searching only the
/// shelf would answer "nothing matched" for almost every film there is, which is the opposite
/// of what that setting is for.
export default function SearchPage() {
  const { t } = useTranslation();
  const [parameters] = useSearchParams();
  const term = parameters.get('q') ?? '';

  const discovery = useDiscoveryState();
  const [opened, setOpened] = useState<CatalogueEntry>();
  const open = useOpenCatalogueEntry(setOpened);

  const results = useApi(
    ['items', 'search', term],
    () => api.items({ search: term, take: 100 }),

    // Nothing typed, nothing asked for. The empty result below is what the screen shows then.
    { enabled: term.length > 0 },
  );

  // The catalogue is searched for the same word, under the same condition that made it the
  // library in the first place.
  const onDemand = discovery.hasCatalogue && Boolean(discovery.playsOnDemand);
  const searchesCatalogue = discovery.hasCatalogue && (discovery.catalogueInLibrary || onDemand);

  const catalogue = useApi(
    ['discovery', 'catalogue', 'search', term],
    () => api.catalogue({ query: term, kind: 'Any' }),
    { enabled: searchesCatalogue && term.length > 0, staleTime: 5 * 60 * 1000 },
  );

  // Live television is searched too, from every tuner plugin: channels by name, and what the
  // guide says is on, now and in the days ahead.
  const { can } = useAuth();
  const live = useApi(
    ['livetv', 'search', term],
    () => api.liveTvSearch(term),
    { enabled: can('ViewLiveTv') && term.length > 0, staleTime: 60_000 },
  );
  const liveFound = Boolean(live.data && (live.data.channels.length > 0 || live.data.programmes.length > 0));

  const held = results.data?.items ?? [];
  const found = catalogue.data?.items ?? [];

  // Where everything plays on demand there is one answer, not two: a film this server holds and
  // the catalogue's entry for the same film are the same result, and the held one is the one
  // shown - it has the progress bar and the page with its versions.
  const heldIds = new Set(held.map((item) => item.id));
  const merged = found.filter((entry) => !entry.libraryItemId || !heldIds.has(entry.libraryItemId));

  return (
    <Stack spacing={3}>
      <Stack direction="row" spacing={2} sx={{ alignItems: 'baseline' }}>
        <Typography variant="h2">{t('search.title')}</Typography>

        {/* The count is the library's, so it is only shown where the library is the whole
            answer. Beside a catalogue of twenty results, "0 entries" reads as "nothing found"
            and is simply wrong. */}
        {results.data && term && !searchesCatalogue && !onDemand && !liveFound && (
          <Typography color="text.secondary">{t('items.count', { count: results.data.total })}</Typography>
        )}
      </Stack>

      {term && (
        <Typography color="text.secondary">{t('search.for', { term })}</Typography>
      )}

      {onDemand ? (
        <AsyncSection
          loading={results.loading || catalogue.loading}
          error={catalogue.error ?? results.error}
          onRetry={() => {
            results.reload();
            catalogue.reload();
          }}
        >
          {held.length === 0 && merged.length === 0 ? (
            !liveFound && <Typography color="text.secondary">{term ? t('search.nothing') : t('search.prompt')}</Typography>
          ) : (
            <Box
              sx={{
                display: 'grid',
                gap: 2,
                gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
              }}
            >
              {held.map((item) => (
                <MediaCard key={item.id} item={item} />
              ))}

              {merged.map((entry) => (
                <CatalogueCard
                  key={`${entry.title.provider}:${entry.title.kind}:${entry.title.id}`}
                  entry={entry}
                  showHeld={false}
                  onOpen={() => open(entry)}
                />
              ))}
            </Box>
          )}
        </AsyncSection>
      ) : searchesCatalogue ? (
        <>
          {/* What is actually here first: these play. The catalogue below is everything else
              that exists, and a tile of it is a thing to go and get. */}
          {held.length > 0 && (
            <Stack spacing={1.5}>
              <Typography variant="h3">{t('library.mode.mine')}</Typography>
              <MediaGrid items={held} emptyKey="search.nothing" />
            </Stack>
          )}

          <Stack spacing={1.5}>
            <Typography variant="h3">{t('library.mode.catalogue')}</Typography>

            <AsyncSection
              loading={catalogue.loading}
              error={catalogue.error}
              onRetry={catalogue.reload}
            >
              {found.length === 0 ? (
                <Typography color="text.secondary">
                  {term ? t('search.nothing') : t('search.prompt')}
                </Typography>
              ) : (
                // Films and programmes apart. The catalogue answers a word with both, and one
                // list of them woven together reads as neither.
                <Stack spacing={3}>
                  {(['Movie', 'Series'] as const).map((kind) => {
                    const ofKind = found.filter((entry) => entry.title.kind === kind);

                    return ofKind.length === 0 ? null : (
                      <Stack key={kind} spacing={1}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          {t(kind === 'Movie' ? 'discover.kind.movie' : 'discover.kind.series')}
                        </Typography>

                        <Box
                          sx={{
                            display: 'grid',
                            gap: 2,
                            gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
                          }}
                        >
                          {ofKind.map((entry) => (
                            <CatalogueCard
                              key={`${entry.title.provider}:${entry.title.kind}:${entry.title.id}`}
                              entry={entry}
                              onOpen={() => open(entry)}
                            />
                          ))}
                        </Box>
                      </Stack>
                    );
                  })}
                </Stack>
              )}
            </AsyncSection>
          </Stack>
        </>
      ) : (
        <AsyncSection loading={results.loading} error={results.error} onRetry={results.reload}>
          {/* "No results" is not true when the answer is a channel. */}
          {(held.length > 0 || !liveFound) && (
            <MediaGrid items={held} emptyKey={term ? 'search.nothing' : 'search.prompt'} />
          )}
        </AsyncSection>
      )}

      {live.data && liveFound && <LiveTvSearchResults result={live.data} />}

      <CatalogueDialog
        entry={opened}
        onClose={() => setOpened(undefined)}
        onFetched={() => {
          catalogue.reload();
          results.reload();
        }}
      />
    </Stack>
  );
}
